import { useEffect, useRef, useState } from "react";

import { Box, Container, Flex, Grid, Heading, Link, Select, Stack, Text, useToast } from "@chakra-ui/react";
import { useUser } from "@clerk/clerk-react";
import { PhCalculationRequest, PhCalculationResponse } from "@henophess/types";
import { Link as RouterLink } from "react-router-dom";

import { fetchGroupMembershipId, fetchMembers } from "~/api/members";
import { AuthenticationRequiredError, savePhData, submitPhCalculation } from "~/api/ph";
import { PhCalculationInputForm, PhCalculationInputFormRef } from "~/components/tools/PhCalculationInputForm";
import { PhMeasurementsChart } from "~/components/tools/PhChart";
import { PhRecommendationsDisplay } from "~/components/tools/PhRecommendationsDisplay";
import { PhTestControls } from "~/components/tools/PhTestControls";
import { SymptomsBox } from "~/components/tools/SymptomsBox";
import { useAppState } from "~/hooks/useAppState";
import { Member } from "~/store";
import { dispatchPhAnalysisCompleted } from "~/utils/cacheInvalidation";

export const PhAnalysisPage = () => {
  const [analysis, setAnalysis] = useState<PhCalculationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef<PhCalculationInputFormRef>(null);
  const toast = useToast();
  const { isSignedIn, isLoaded } = useUser();
  const { userHomeData } = useAppState("userHomeData");
  const [isLoadingAppState, setIsLoadingAppState] = useState(true);

  // Debug log when userHomeData changes
  useEffect(() => {
    console.log("PhAnalysisPage: userHomeData updated", {
      hasData: !!userHomeData,
      defaultGroup: userHomeData?.defaultGroup,
      isLoading: isLoadingAppState
    });

    // If userHomeData is defined, we're no longer loading
    if (userHomeData !== undefined) {
      setIsLoadingAppState(false);
    }
  }, [userHomeData, isLoadingAppState]);

  // Fetch members when the component mounts and user is signed in
  useEffect(() => {
    const loadMembers = async () => {
      if (isSignedIn) {
        setIsLoadingMembers(true);
        try {
          const membersList = await fetchMembers();
          setMembers(membersList);
        } catch (err) {
          console.error("Failed to load members:", err);
        } finally {
          setIsLoadingMembers(false);
        }
      }
    };

    if (isLoaded) {
      loadMembers();
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "t" && !event.repeat && document.activeElement?.tagName !== "INPUT") {
        setIsTestMode((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  const handleSubmit = async (data: PhCalculationRequest) => {
    try {
      const result = await submitPhCalculation(data);
      setAnalysis(result as PhCalculationResponse);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleSave = async (data: PhCalculationRequest) => {
    // First check if user is signed in to provide a better UX
    if (isLoaded && !isSignedIn) {
      toast({
        title: "Login Required",
        description: (
          <>
            Please{" "}
            <Link as={RouterLink} to="/signin" color="blue.500" fontWeight="bold">
              log in
            </Link>{" "}
            to save your pH data
          </>
        ),
        status: "warning",
        duration: 5000,
        isClosable: true
      });
      setError("Please log in to save your pH data");
      return;
    }

    // Check if a member is selected
    if (!selectedMemberId) {
      toast({
        title: "No Member Selected",
        description: "Please select a member to save pH data",
        status: "warning",
        duration: 5000,
        isClosable: true
      });
      setError("Please select a member to save pH data");
      return;
    }

    // Set saving flag
    setIsSaving(true);

    try {
      // Get the corresponding group member ID
      const groupMemberId = await fetchGroupMembershipId(selectedMemberId);

      if (!groupMemberId) {
        console.error(`Could not find group membership for member ${selectedMemberId}`);
        toast({
          title: "Member Not Available",
          description: "Could not retrieve membership information. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        setError("Could not retrieve membership information. Please try again.");
        setIsSaving(false);
        return;
      }

      // Now use the group member ID for saving the pH data
      console.log(`Saving pH data for group member ID: ${groupMemberId}`);
      const result = await savePhData(data, groupMemberId);

      // Set the analysis results, just like when calculating
      setAnalysis(result as PhCalculationResponse);

      // Dispatch the event to invalidate recommendations
      dispatchPhAnalysisCompleted(groupMemberId);

      // Check if the result contains a duplicate flag
      if (result.duplicate) {
        // Show duplicate warning toast
        toast({
          title: "Note: Similar data already exists",
          description: result.message || "Similar pH data was already saved within ±2 hours of the testing date",
          status: "info",
          duration: 5000,
          isClosable: true
        });
      } else {
        // Show success toast
        toast({
          title: "pH data saved successfully!",
          status: "success",
          duration: 3000,
          isClosable: true
        });
      }

      setError(null);
    } catch (err) {
      console.error("Error saving pH data:", err);
      // Special handling for auth errors
      if (err instanceof AuthenticationRequiredError) {
        toast({
          title: "Login Required",
          description: (
            <>
              Please{" "}
              <Link as={RouterLink} to="/signin" color="blue.500" fontWeight="bold">
                log in
              </Link>{" "}
              to save your pH data
            </>
          ),
          status: "warning",
          duration: 5000,
          isClosable: true
        });
        setError("Please log in to save your pH data");
      } else {
        // General error handling
        toast({
          title: "Error saving pH data",
          description: err instanceof Error ? err.message : "An error occurred",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestData = (data: PhCalculationRequest) => {
    if (formRef.current) {
      formRef.current.populate(data);
    }
  };

  return (
    <Box minH="100vh">
      <Container maxW="container.xl" py={4}>
        <Stack spacing={6}>
          <Flex justify="center" p={4} direction="column" align="center" gap={6}>
            <Heading as="h1" size="xl">
              pH Analysis
            </Heading>
            {isTestMode && <PhTestControls onTestDataGenerated={handleTestData} showTrendControls={false} />}
          </Flex>

          {/* Member selection dropdown */}
          {isSignedIn && (
            <Box width="100%" maxW="300px" mx="auto" mb={4}>
              <Text mb={2} fontWeight="medium">
                Select a member to save pH data
              </Text>
              <Select
                placeholder="Select a member"
                value={selectedMemberId || ""}
                onChange={(e) => setSelectedMemberId(e.target.value ? Number(e.target.value) : null)}
                isDisabled={isLoadingMembers || isSaving}
              >
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </Select>
            </Box>
          )}

          <Box width="100%">
            <PhCalculationInputForm
              onSubmit={handleSubmit}
              onSave={isSignedIn ? handleSave : undefined}
              ref={formRef}
              isSaving={isSaving}
            />
          </Box>

          {error && (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          )}

          {analysis && (
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={8} alignItems="start">
              <PhRecommendationsDisplay
                supplementRecommendations={analysis.supplementRecommendations || []}
                textRecommendations={analysis.textResponses || []}
              />
              <PhMeasurementsChart analysis={analysis} />
            </Grid>
          )}

          {analysis && (
            <Box width="100%">
              <Grid templateColumns={{ base: "1fr", md: "1fr" }} gap={4}>
                <Box>
                  <SymptomsBox symptoms={analysis.symptoms} />
                </Box>
              </Grid>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default PhAnalysisPage;
